import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import ReceiptCameraCapture from '../../components/kiosk/ReceiptCameraCapture';
import { expenseReimbursementService } from '../../services/expenseReimbursementService';
import axios from 'axios';
import { formatCurrency } from '../../components/CurrencyTextField';
import { formatDateDisplay } from '../../utils/dateUtils';

const statusColor = (status) => {
  if (status === 'approved' || status === 'paid') return 'success';
  if (status === 'rejected') return 'error';
  return 'warning';
};

const EmployeeReceipts = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitOpen, setSubmitOpen] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [amount, setAmount] = useState('');
  const [receiptDate, setReceiptDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const res = await expenseReimbursementService.listMine();
      setItems(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load receipts');
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await load();
      setLoading(false);
    })();
  }, []);

  const resetSubmitForm = () => {
    setPhotoFile(null);
    setNotes('');
    setAmount('');
    setReceiptDate('');
    setSubmitOpen(false);
  };

  const parseAmountInput = (raw) => {
    const n = parseFloat(String(raw ?? '').replace(/[$,\s]/g, '').trim());
    return Number.isFinite(n) ? n : NaN;
  };

  const amountValue = parseAmountInput(amount);
  const amountValid = Number.isFinite(amountValue) && amountValue > 0;
  const notesValid = notes.trim().length > 0;
  const canSubmit = Boolean(photoFile) && amountValid && notesValid && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await expenseReimbursementService.submitMine({
        file: photoFile,
        notes: notes.trim(),
        amount: amountValue,
        receiptDate: receiptDate || undefined,
      });
      setSuccess('Receipt submitted successfully.');
      resetSubmitForm();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit receipt');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          My Receipts
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setSubmitOpen(true)}>
          Submit receipt
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Submitted</TableCell>
                <TableCell>Receipt date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">View</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No receipts submitted yet.</TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.submitted_at ? formatDateDisplay(row.submitted_at) : '—'}</TableCell>
                    <TableCell>{row.receipt_date ? formatDateDisplay(row.receipt_date) : '—'}</TableCell>
                    <TableCell>{row.amount != null ? formatCurrency(row.amount) : '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} color={statusColor(row.status)} />
                    </TableCell>
                    <TableCell>{row.notes || '—'}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={async () => {
                          try {
                            const response = await axios.get(expenseReimbursementService.getMyImageUrl(row.id), {
                              responseType: 'blob',
                            });
                            const url = URL.createObjectURL(response.data);
                            window.open(url, '_blank', 'noopener,noreferrer');
                            setTimeout(() => URL.revokeObjectURL(url), 60000);
                          } catch (err) {
                            setError(err.response?.data?.message || err.message || 'Failed to open receipt');
                          }
                        }}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={submitOpen} onClose={() => !submitting && resetSubmitForm()} maxWidth="sm" fullWidth>
        <DialogTitle>Submit expense receipt</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Take a photo of your receipt or choose an image from your device.
          </Typography>
          <ReceiptCameraCapture onCapture={setPhotoFile} disabled={submitting} />
          <TextField
            fullWidth
            size="small"
            label="Receipt date (optional)"
            type="date"
            value={receiptDate}
            onChange={(e) => setReceiptDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            required
            label="Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={amount.trim() !== '' && !amountValid}
            helperText={
              amount.trim() !== '' && !amountValid
                ? 'Enter an amount greater than 0'
                : undefined
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            required
            label="Job/Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={resetSubmitForm} disabled={submitting}>Cancel</Button>
          <Button variant="contained" disabled={!canSubmit} onClick={handleSubmit}>
            {submitting ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeReceipts;
